(function () {
  const cartRoot = globalThis.Shopify?.routes?.root ?? '/';
  const SUCCESS_MESSAGE_KEY = 'b2bOrderPlacedMessage';

  /**
   * The text for a 409 insufficient_stock response: the intro, then one line per shortage,
   * "<sku, or title when sku is blank>: kun <available> på lager", joined with "\n".
   * @param {Array<{ sku: string|null, title: string|null, available: number }>} shortages
   * @param {{ intro: string, line: string }} strings - line holds {{ label }} and {{ available }}
   * @returns {string|null} null when no entry has a label
   */
  function shortageMessage(shortages, strings) {
    if (!Array.isArray(shortages)) return null;
    const lines = [];
    for (const entry of shortages) {
      if (!entry || typeof entry !== 'object') continue;
      const sku = typeof entry.sku === 'string' ? entry.sku.trim() : '';
      const title = typeof entry.title === 'string' ? entry.title.trim() : '';
      const label = sku || title;
      if (!label) continue;
      const available = Math.max(0, Math.floor(Number(entry.available)) || 0);
      lines.push(strings.line.split('{{ label }}').join(label).split('{{ available }}').join(String(available)));
    }
    if (!lines.length) return null;
    return [strings.intro, ...lines].join('\n');
  }

  if (typeof module === 'object' && module.exports) module.exports = { shortageMessage };
  if (typeof HTMLElement === 'undefined' || typeof customElements === 'undefined') return;

  class B2BCheckout extends HTMLElement {
    #submitButton;
    #message;
    #refreshes = 0;
    #onCartUpdate = () => this.#refreshLines();

    connectedCallback() {
      this.#submitButton = this.querySelector('[data-ref="submitButton"]');
      this.#message = this.querySelector('[data-ref="message"]');
      this.#submitButton?.addEventListener('click', () => this.#handleSubmit());
      document.addEventListener('cart-update', this.#onCartUpdate);
      this.#loadCompanyAddress();
    }

    disconnectedCallback() {
      document.removeEventListener('cart-update', this.#onCartUpdate);
    }

    // Re-renders this section (Section Rendering API, `section_id`) and replaces the lines and totals card
    // in place; the address and reference fields are left as they are. When the new render has no lines
    // card (empty cart), the whole checkout is replaced. Only the response to the latest cart-update is used.
    async #refreshLines() {
      const sectionId = this.dataset.sectionId;
      if (!sectionId) return;
      const request = ++this.#refreshes;
      try {
        const response = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(sectionId)}`);
        if (!response.ok) return;
        const html = await response.text();
        if (request !== this.#refreshes || !this.isConnected) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const lines = doc.querySelector('[data-b2b-checkout-lines]');
        const current = this.querySelector('[data-b2b-checkout-lines]');
        if (lines && current) {
          current.replaceWith(document.importNode(lines, true));
          return;
        }
        const checkout = doc.querySelector('.b2b_checkout');
        const wrapper = this.closest('.b2b_checkout');
        if (checkout && wrapper) wrapper.replaceWith(document.importNode(checkout, true));
      } catch (error) {
        console.error('B2B checkout refresh failed:', error);
      }
    }

    async #loadCompanyAddress() {
      const target = this.querySelector('[data-ref="senderAddress"]') || this.querySelector('[data-ref="recipientAddress"]');
      if (!target) return;

      try {
        const response = await fetch('/apps/b2b-orders/context');
        const data = await response.json();
        if (!data.ok || !data.address) {
          target.textContent = this.dataset.i18nAddressError;
          return;
        }
        target.textContent = this.#formatAddress(data.address);
      } catch (error) {
        target.textContent = this.dataset.i18nAddressError;
      }
    }

    #formatAddress(address) {
      const lines = [
        [address.firstName, address.lastName].filter(Boolean).join(' '),
        address.company,
        address.address1,
        address.address2,
        [address.zip, address.city].filter(Boolean).join(' '),
        address.countryCode,
        address.phone,
      ].filter(Boolean);
      return lines.join('\n');
    }

    async #handleSubmit() {
      const isDropship = this.dataset.orderType === 'dropship';

      this.#setMessage('', null);

      let dropshipAddress = null;
      if (isDropship) {
        dropshipAddress = this.#readDropshipAddress();
        if (!dropshipAddress) {
          this.#setMessage(this.dataset.i18nValidationError, 'error');
          return;
        }
      }

      const reference = this.querySelector('[data-ref="reference"]')?.value.trim() || '';

      this.#submitButton.disabled = true;
      this.#setMessage(this.dataset.i18nSending, null);

      try {
        const cartResponse = await fetch(`${cartRoot}cart.js`);
        const cart = await cartResponse.json();
        if (!cart.items.length) {
          this.#setMessage(this.dataset.i18nCartEmpty, 'error');
          this.#submitButton.disabled = false;
          return;
        }
        const lineItems = cart.items.map((item) => {
          const forventetLevering = item.properties?.['Forventet levering'];
          return {
            variantId: `gid://shopify/ProductVariant/${item.variant_id}`,
            quantity: item.quantity,
            ...(typeof forventetLevering === 'string' && forventetLevering.trim() ? { forventetLevering } : {}),
          };
        });

        const response = await fetch('/apps/b2b-orders/orders/create', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderType: isDropship ? 'dropship' : 'stock',
            lineItems,
            ...(cart.note ? { note: cart.note } : {}),
            ...(reference ? { reference } : {}),
            ...(dropshipAddress ? { dropshipAddress } : {}),
          }),
        });
        const data = await response.json();

        if (response.status === 409 && data.error === 'insufficient_stock') {
          const text = shortageMessage(data.shortages, {
            intro: this.dataset.i18nInsufficientStock,
            line: this.dataset.i18nInsufficientStockLine,
          });
          if (text) {
            this.#setMessage(text, 'error');
            this.#submitButton.disabled = false;
            return;
          }
        }

        if (!data.ok) {
          this.#setMessage(this.dataset.i18nOrderFailed.replace('{{ error }}', data.error || 'ukendt fejl'), 'error');
          this.#submitButton.disabled = false;
          return;
        }

        await fetch(`${cartRoot}cart/clear.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        sessionStorage.setItem(SUCCESS_MESSAGE_KEY, this.dataset.i18nSuccessMessage);
        window.location.href = `${cartRoot}cart`;
      } catch (error) {
        this.#setMessage(this.dataset.i18nGenericError, 'error');
        this.#submitButton.disabled = false;
      }
    }

    #readDropshipAddress() {
      const get = (name) => this.querySelector(`[data-ref="${name}"]`);
      const firstName = get('firstName');
      const lastName = get('lastName');
      const address1 = get('address1');
      const address2 = get('address2');
      const city = get('city');
      const zip = get('zip');
      const countryCode = get('countryCode');
      const phone = get('phone');
      if (!firstName || !lastName || !address1 || !city || !zip || !countryCode) return null;

      const required = [firstName, lastName, address1, city, zip, countryCode];
      if (required.some((field) => !field.value.trim())) return null;

      return {
        firstName: firstName.value.trim(),
        lastName: lastName.value.trim(),
        address1: address1.value.trim(),
        address2: address2?.value.trim() || undefined,
        city: city.value.trim(),
        zip: zip.value.trim(),
        countryCode: countryCode.value.trim().toUpperCase(),
        phone: phone?.value.trim() || undefined,
      };
    }

    #setMessage(text, state) {
      if (!this.#message) return;
      this.#message.textContent = text;
      if (state) {
        this.#message.dataset.state = state;
      } else {
        delete this.#message.dataset.state;
      }
    }
  }

  if (!customElements.get('b2b-checkout-component')) {
    customElements.define('b2b-checkout-component', B2BCheckout);
  }
})();
